import {
  Button,
  Divider,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import Modal, { CommonModalProps } from "..";
import { orderService } from "../../../Core/Api/services";
import { ApplyManualChangesOnItemByIdsRequest } from "../../../Core/Api/Types/apply-manual-changes-on-item-by-ids-request";
import { OrderStatus } from "../../../Core/Api/Types/order-status";
import { OrderItemProductNonDoableFlag } from "../../../Core/Api/Types/order-item-product-non-doable-flag";
import { DbOrderItemInfos } from "../../../Core/Api/Types/db-order-item-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import WarningIcon from "@mui/icons-material/Warning";
import CheckIcon from "@mui/icons-material/Check";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import OutlinedCheckbox from "../../Forms/OutlinedCheckbox";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";
import ErrorIcon from "@mui/icons-material/Error";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { useTranslation } from "react-i18next";

export type ModalOrderManualChangesElementProps = {
  onClose?: () => void;
  items?: DbOrderItemInfos[];
  itemIds?: number[];
};

export type ModalOrderManualChangesProps = CommonModalProps<ModalOrderManualChangesElementProps>;

const ModalOrderManualChanges = React.forwardRef<any, ModalOrderManualChangesProps>((props, ref) => {
  const { t } = useTranslation();
  const { items, itemIds, closeModal, onClose } = props;
  const enqueueCustomSnackbar = useCustomSnackbar();

  const length = items ? items.length : itemIds ? itemIds.length : 0;

  const [status, setStatus] = useState<OrderStatus>(
    items && items.length === 1 ? items[0].status! : OrderStatus.COMPLETED
  );
  const [message, setMessage] = useState<string>(items && items.length === 1 ? items[0].message! : "");
  const [doable, setDoable] = useState<boolean>(items && items.length === 1 ? items[0].doable! : true);
  const [flag, setFlag] = useState<OrderItemProductNonDoableFlag>(
    items && items.length === 1
      ? items[0].nonDoableFlag
        ? items[0].nonDoableFlag
        : OrderItemProductNonDoableFlag.Unknown
      : OrderItemProductNonDoableFlag.Unknown
  );

  const handleCancelClick: React.MouseEventHandler = () => {
    closeModal();
  };

  const handleConfirmClick = () => {
    let request: ApplyManualChangesOnItemByIdsRequest = {
      itemIds: items ? items.map((item) => item.id) : itemIds!,
      status: status,
      message: message,
      doable: doable,
      nonDoableFlag: flag === OrderItemProductNonDoableFlag.Unknown ? null : flag,
    };

    orderService.ApplyManualChangesOnItemByIds(request).then((response) => {
      onClose && onClose();
      closeModal();
      enqueueCustomSnackbar(t("alert:success.itemsUpdatedSuccessfully", { count: length }), "success");
    });
  };

  return (
    <Modal
      title={t("modal:title.applyManualChangesOnOrderItem", { count: length })}
      reference={ref}
      closeModal={closeModal}
      width={600}
      footer={
        <Stack direction="row" justifyContent="space-between" spacing={1}>
          <Stack direction="row" spacing={1.5}>
            <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          </Stack>
          <Button variant="contained" onClick={() => handleConfirmClick()}>
            {t("common:done")}
          </Button>
        </Stack>
      }
    >
      <Stack mb={3}>
        <Typography mb="6px" fontWeight={500}>
          {t("common:status")}
        </Typography>
        <Select
          displayEmpty
          value={status}
          size="small"
          onChange={(e) => setStatus(OrderStatus[e.target.value])}
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        >
          <MenuItem value={OrderStatus.CREATED}>
            <ListItemIcon>
              <AddCircleIcon />
            </ListItemIcon>
            <ListItemText>{t("common:created")}</ListItemText>
          </MenuItem>
          <MenuItem value={OrderStatus.RUNNING}>
            <ListItemIcon>
              <DirectionsRunIcon />
            </ListItemIcon>
            <ListItemText>{t("common:running")}</ListItemText>
          </MenuItem>
          <MenuItem value={OrderStatus.COMPLETED}>
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
            <ListItemText>{t("common:completed")}</ListItemText>
          </MenuItem>
          <MenuItem value={OrderStatus.STANDBY}>
            <ListItemIcon>
              <WarningIcon />
            </ListItemIcon>
            <ListItemText>{t("common:standby")}</ListItemText>
          </MenuItem>
          <MenuItem value={OrderStatus.ABORTED}>
            <ListItemIcon>
              <ErrorIcon />
            </ListItemIcon>
            <ListItemText>{t("common:aborted")}</ListItemText>
          </MenuItem>
        </Select>
      </Stack>
      <Stack mb={3}>
        <Typography mb="6px" fontWeight={500}>
          {t("modal:body.doabilityFlag")}
        </Typography>
        <Select
          displayEmpty
          value={flag}
          size="small"
          MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
          onChange={(e) => setFlag(OrderItemProductNonDoableFlag[e.target.value])}
        >
          <MenuItem value={OrderItemProductNonDoableFlag.Unknown}>
            <ListItemIcon>
              <CheckIcon />
            </ListItemIcon>
            <ListItemText>{t("menu:item.noFlag")}</ListItemText>
          </MenuItem>

          <MenuItem value={OrderItemProductNonDoableFlag.MissingAssets}>
            <ListItemIcon>
              <WarningIcon />
            </ListItemIcon>
            <ListItemText>{t("menu:item.missingAssets")}</ListItemText>
          </MenuItem>

          <MenuItem value={OrderItemProductNonDoableFlag.CandidateAssets}>
            <ListItemIcon>
              <ShoppingCartIcon />
            </ListItemIcon>
            <ListItemText>{t("menu:item.candidateAssets")}</ListItemText>
          </MenuItem>
        </Select>
      </Stack>
      <Stack>
        <OutlinedCheckbox direction="row-reverse" checked={doable} onClick={() => setDoable(!doable)}>
          <Typography fontWeight={500}>{t("menu:item.thisOrderItemIsDoable")}</Typography>
        </OutlinedCheckbox>
      </Stack>
      <Divider sx={{ mt: 3, mb: 3 }} />
      <Stack>
        <FormControl>
          <InputLabel shrink htmlFor="placeholdername">
            {t("common:comment")}
          </InputLabel>
          <OutlinedInput
            placeholder={t("field:placeholder.enterYourCommentHere")}
            value={message}
            multiline
            rows={3}
            onChange={(e) => setMessage(e.target.value)}
          />
        </FormControl>
      </Stack>
    </Modal>
  );
});

export default ModalOrderManualChanges;
